import { useContext, useEffect, useCallback } from 'react'
import { message } from 'antd'
import { ConfigContext } from '../context/configContext'
import {
  useQueryAdminPartnerById,
  useMutateAdminUpdatePartnerAction,
  useMutateAdminUpdatePartnerDocs,
  useMutateAdminUpdatePartnerSecurityStatus,
  useMutateAdminUpdatePartnerKind,
  useQueryAdminPartnerProfileFormById,
  useQueryAdminPartnerByPhone
} from '../api/react-query/adminPartners'
import { oneMinute } from '../Constants'
import { ADMIN_PARTNERS_PAGE } from '../routes'
import { adminReducerActions, partnerStatusCodes } from '../AppConfig'
import { useGetPartnerId } from './useGetPartnerId'

export const usePartner = partnerPhone => {
  const partnerId = useGetPartnerId()
  const {
    loadingData: {
      isLoadingAdminUpdateShopperStatus,
      isLoadingAdminUpdateBddStatus,
      isLoadingAdminUpdatePartnerSecurityStatus
    },
    modalData: { isEditProfileInfoRequest, refetchProfileInfo },
    isExternalCoordinator,
    setConfig
  } = useContext(ConfigContext)

  const {
    data: adminPartnerByPhoneData,
    isError: isErrorAdminPartnerByPhone,
    isLoading: isLoadingAdminPartnerByPhone,
    isSuccess: isSuccessAdminPartnerByPhone
  } = useQueryAdminPartnerByPhone(
    {
      phone: partnerPhone
    },
    {
      enabled: !!partnerPhone,
      refetchInterval: oneMinute
    }
  )

  useEffect(() => {
    if (isSuccessAdminPartnerByPhone && adminPartnerByPhoneData?.partner) {
      setConfig({
        type: adminReducerActions.updatePartner,
        value: adminPartnerByPhoneData?.partner
      })
    }
  }, [isSuccessAdminPartnerByPhone, adminPartnerByPhoneData, setConfig])

  useEffect(() => {
    if (isErrorAdminPartnerByPhone) {
      message.error('Ошибка доступа к данным')
    }
  }, [isErrorAdminPartnerByPhone])

  const {
    data: adminPartnerByIdData,
    isError: isErrorAdminPartnerById,
    isLoading: isLoadingAdminPartnerById,
    isSuccess: isSuccessAdminPartnerById
  } = useQueryAdminPartnerById(
    {
      partnerId,
      logHeader: 'Partner list'
    },
    {
      enabled: !!partnerId,
      refetchInterval: oneMinute
    }
  )

  useEffect(() => {
    if (isSuccessAdminPartnerById && adminPartnerByIdData?.partner) {
      setConfig({ type: adminReducerActions.updatePartner, value: adminPartnerByIdData?.partner })
    }
  }, [isSuccessAdminPartnerById, adminPartnerByIdData, setConfig])

  useEffect(() => {
    if (isErrorAdminPartnerById) {
      message.error('Ошибка доступа к данным')
    }
  }, [isErrorAdminPartnerById])

  const {
    data: adminPartnerFormByIdData,
    isError: isErrorAdminPartnerFormById,
    isLoading: isLoadingAdminPartnerFormById,
    isSuccess: isSuccessAdminPartnerFormById
  } = useQueryAdminPartnerProfileFormById(
    {
      partnerId,
      refetchProfileInfo
    },
    {
      enabled: Boolean(isEditProfileInfoRequest),
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0
    }
  )

  useEffect(() => {
    if (isSuccessAdminPartnerFormById && adminPartnerFormByIdData?.data) {
      setConfig({
        type: adminReducerActions.setPartnerFromData,
        value: adminPartnerFormByIdData.data
      })
    } else if (isErrorAdminPartnerFormById) {
      message.error('Ошибка доступа к данным анкеты')
    }
  }, [
    isSuccessAdminPartnerFormById,
    isErrorAdminPartnerFormById,
    adminPartnerFormByIdData,
    setConfig
  ])

  const { isLoading: isLoadingAdminUpdatePartnerDocs, mutateAsync: mutateAdminUpdatePartnerDocs } =
    useMutateAdminUpdatePartnerDocs()

  const {
    isSuccess: isSuccessAdminUpdatePartnerSecurityStatus,
    isError: isErrorAdminUpdatePartnerSecurityStatus,
    data: dataAdminUpdatePartnerSecurityStatus,
    mutate: mutateAdminUpdatePartnerSecurityStatus
  } = useMutateAdminUpdatePartnerSecurityStatus()

  useEffect(() => {
    if (isSuccessAdminUpdatePartnerSecurityStatus) {
      if (dataAdminUpdatePartnerSecurityStatus?.errorMessage) {
        return message.error(dataAdminUpdatePartnerSecurityStatus?.errorMessage)
      }
      const msg = `Статус СБ партнера успешно изменен на "${dataAdminUpdatePartnerSecurityStatus?.partner?.profile?.securityCheck?.status}"`
      message.success(msg)
    }
    if (isErrorAdminUpdatePartnerSecurityStatus) {
      message.error(
        dataAdminUpdatePartnerSecurityStatus?.errorMessage ||
          'Не удалось изменить статус СБ партнеру'
      )
    }
  }, [
    isSuccessAdminUpdatePartnerSecurityStatus,
    isErrorAdminUpdatePartnerSecurityStatus,
    dataAdminUpdatePartnerSecurityStatus
  ])

  // const handleCommit = async (section, outcome, comment, cb) => {
  //   if (outcome) {
  //     try {
  //       const result = await mutateAdminUpdatePartnerDocs({
  //         id: partnerId,
  //         outcome,
  //         body: {
  //           documentType: section,
  //           comment
  //         }
  //       })
  //       if (result?.isSuccess) {
  //         setConfig({ type: adminReducerActions.updatePartner, value: result?.partner })
  //         cb?.()
  //         if (
  //           isExternalCoordinator &&
  //           result?.partner?.profile?.statusCode !== partnerStatusCodes.verification
  //         ) {
  //           message.success('Партнер проверен')
  //           window.location.replace(ADMIN_PARTNERS_PAGE)
  //         }
  //       } else {
  //         message.error(result?.errorMessage || 'Ошибка доступа к данным')
  //       }
  //     } catch (error) {
  //       console.error(error)
  //     }
  //   }
  // }

  const handleCommit = useCallback(
    async (section, outcome, comment, cb) => {
      if (outcome) {
        try {
          const result = await mutateAdminUpdatePartnerDocs({
            id: partnerId,
            outcome,
            body: {
              documentType: section,
              comment
            }
          })
          if (result?.isSuccess) {
            setConfig({ type: adminReducerActions.updatePartner, value: result?.partner })
            cb?.()
            if (
              isExternalCoordinator &&
              result?.partner?.profile?.statusCode !== partnerStatusCodes.verification &&
              result?.partner?.profile?.statusCode !== partnerStatusCodes.selfFeeCheck
            ) {
              message.success('Партнер проверен')
              history.replace(ADMIN_PARTNERS_PAGE)
            }
          } else {
            message.error(result?.errorMessage || 'Ошибка доступа к данным')
          }
        } catch (error) {
          console.error(error)
        }
      }
    },
    [isExternalCoordinator, mutateAdminUpdatePartnerDocs, partnerId, setConfig]
  )

  const {
    isLoading: isLoadingAdminUpdatePartnerAction,
    mutateAsync: mutateAdminUpdatePartnerAction
  } = useMutateAdminUpdatePartnerAction()

  const handlePartnerActions = async (type, action) => {
    if (action && type) {
      try {
        const result = await mutateAdminUpdatePartnerAction({ id: partnerId, type, action })
        if (result?.isSuccess) {
          setConfig({ type: adminReducerActions.updatePartner, value: result?.partner })
        } else {
          message.error(result?.errorMessage || 'Ошибка доступа к данным')
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  const {
    isLoading: isLoadingAdminUpdatePartnerKind,
    isSuccess: isSuccessAdminUpdatePartnerKind,
    data: dataAdminUpdatePartnerKind,
    mutateAsync: mutateAdminUpdatePartnerKind
  } = useMutateAdminUpdatePartnerKind()

  useEffect(() => {
    if (isSuccessAdminUpdatePartnerKind) {
      if (dataAdminUpdatePartnerKind?.isSuccess) {
        setConfig({
          type: adminReducerActions.updatePartner,
          value: dataAdminUpdatePartnerKind?.partner
        })
      } else {
        message.error(dataAdminUpdatePartnerKind?.errorMessage || 'Ошибка доступа к данным')
      }
    }
  }, [isSuccessAdminUpdatePartnerKind, dataAdminUpdatePartnerKind, setConfig])

  return {
    handleCommit,
    handlePartnerActions,
    mutateAdminUpdatePartnerKind,
    mutateAdminUpdatePartnerSecurityStatus,
    mutateAdminUpdatePartnerDocs,
    isLoading: Boolean(
      isLoadingAdminUpdatePartnerAction ||
        isLoadingAdminUpdateShopperStatus ||
        isLoadingAdminUpdateBddStatus ||
        isLoadingAdminPartnerById ||
        isLoadingAdminPartnerFormById ||
        isLoadingAdminUpdatePartnerDocs ||
        isLoadingAdminUpdatePartnerSecurityStatus ||
        isLoadingAdminUpdatePartnerKind ||
        isLoadingAdminPartnerByPhone
    ),
    isExternalCoordinator
  }
}
